import { useState } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import attendance from 'api/attendance';
import { ErrorsAlert } from 'components/ui/Form/Errors';
import { AppError } from 'utils/AppError';

interface RejectRequestsButtonProps {
  selectedRequests: number[];
  onSuccess: () => void;
  userId: string;
}

export const RejectRequestsButton = ({
  selectedRequests,
  onSuccess,
  userId,
}: RejectRequestsButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const rejectMutation = useMutation(
    (data: { ids: number[]; 'user-id': number }) => attendance.rejectLeave(data),
    {
      onSuccess: (data) => {
        toast.show({
          content: data.message,
          color: 'positive',
        });
        queryClient.invalidateQueries(['attendance']);
        queryClient.invalidateQueries(['attendance-today']);
        setIsModalOpen(false);
        onSuccess();
      },
      onError: (error: typeof AppError) => {
        console.error('Error rejecting requests:', error);
      },
    },
  );

  const handleReject = () => {
    rejectMutation.mutate({ ids: selectedRequests, 'user-id': parseInt(userId, 10) });
  };

  return (
    <>
      <Button
        variant="secondary"
        color="negative"
        isDisabled={selectedRequests.length === 0}
        onClick={() => setIsModalOpen(true)}>
        Reject
      </Button>
      <Modal isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)} size="small">
        <ModalHeader title="Confirm Rejection" />
        <ModalBody>
          <Text>Are you sure you want to reject the selected requests?</Text>
          {rejectMutation.error && (
            <Box marginTop="spacing.4">
              <ErrorsAlert errorResponse={rejectMutation.error} />
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
            <Button
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              isDisabled={rejectMutation.isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              color="negative"
              onClick={handleReject}
              isLoading={rejectMutation.isLoading}>
              Reject
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};
